/**
 * Buffer, persist and flush product-usage events.
 *
 * Everything here is written around one constraint: the caller cannot see a failure.
 * `track()` returns void from a render path, so this module has to decide by itself
 * what to retry, what to drop, and what to do when the process is killed mid-flush.
 *
 * The ordering that matters: the queue is persisted **before** the request goes out,
 * never after. A process killed mid-flight then replays the batch on next launch,
 * and because every event carries a client-minted id the server ignores the
 * duplicates. The opposite order would lose the batch instead, and a lost batch
 * cannot be recovered by anyone.
 *
 * Known hole, stated rather than papered over: on the web, `pagehide` cannot await
 * an async storage write. The mitigation is a one-second persist debounce plus an
 * awaited persist on `visibilitychange → hidden`, which is the signal that actually
 * fires on mobile. A hard kill can still lose under a second of events, which is an
 * acceptable price for never blocking a navigation.
 */

import { ApiError, sendAnalyticsEvents } from "~/lib/api";
import { getItem, removeItem, setItem, StorageKey } from "~/lib/storage";
import type { AnalyticsBatch, AnalyticsEvent } from "~/types/analytics";

/** Beyond this the oldest events are dropped: what just happened matters more. */
const MAX_QUEUE = 200;
const FLUSH_BATCH = 25;
/** Yield after this many batches so a large backlog cannot monopolise the network. */
const MAX_BATCHES_PER_FLUSH = 4;
const FLUSH_INTERVAL_MS = 15_000;
/** A route change must not machine-gun the API on fast navigation. */
const MIN_FLUSH_GAP_MS = 5_000;
/** Matches the server's own staleness rule, so a stale flush is dropped locally. */
const MAX_EVENT_AGE_MS = 24 * 60 * 60 * 1000;
const PERSIST_DEBOUNCE_MS = 1_000;
const MAX_BACKOFF_MS = 60_000;

export type FlushReason =
  "boot" | "size" | "interval" | "navigation" | "background";

interface StoredQueue {
  user_id: string;
  events: AnalyticsEvent[];
}

type BatchMeta = Omit<AnalyticsBatch, "sent_at" | "events">;

let pending: AnalyticsEvent[] = [];
let owner: string | null = null;
let meta: BatchMeta | null = null;
let inFlight = false;
let failures = 0;
let lastFlushAt = 0;
let persistTimer: ReturnType<typeof setTimeout> | undefined;
let retryTimer: ReturnType<typeof setTimeout> | undefined;
let intervalTimer: ReturnType<typeof setInterval> | undefined;

export function configureQueue(userId: string, batchMeta: BatchMeta): void {
  owner = userId;
  meta = batchMeta;
}

export function enqueue(event: AnalyticsEvent): void {
  pending.push(event);
  if (pending.length > MAX_QUEUE) {
    // Drop from the head: the oldest events are both the least interesting and the
    // likeliest to be rejected as stale anyway.
    pending.splice(0, pending.length - MAX_QUEUE);
  }
  schedulePersist();
  if (pending.length >= FLUSH_BATCH) void flush("size");
}

export function queueLength(): number {
  return pending.length;
}

/** Replay whatever a previous run of the app left behind. */
export async function restoreQueue(userId: string): Promise<void> {
  const raw = await getItem(StorageKey.AnalyticsQueue);
  if (!raw) return;
  try {
    const stored = JSON.parse(raw) as StoredQueue;
    if (stored.user_id !== userId) {
      // A queue belonging to the previous member on a shared device is discarded,
      // not re-attributed. Same rule as `clearUserScopedQueries`.
      await removeItem(StorageKey.AnalyticsQueue);
      return;
    }
    const cutoff = Date.now() - MAX_EVENT_AGE_MS;
    pending = stored.events
      .filter((event) => Date.parse(event.occurred_at) >= cutoff)
      .slice(-MAX_QUEUE);
  } catch {
    // A truncated write from a kill mid-persist. Losing telemetry is not an outage.
    await removeItem(StorageKey.AnalyticsQueue);
  }
}

function schedulePersist(): void {
  if (persistTimer !== undefined) return;
  persistTimer = setTimeout(() => {
    persistTimer = undefined;
    void persistNow();
  }, PERSIST_DEBOUNCE_MS);
}

export async function persistNow(): Promise<void> {
  if (persistTimer !== undefined) {
    clearTimeout(persistTimer);
    persistTimer = undefined;
  }
  if (owner === null) return;
  if (pending.length === 0) {
    await removeItem(StorageKey.AnalyticsQueue);
    return;
  }
  const stored: StoredQueue = { user_id: owner, events: pending };
  await setItem(StorageKey.AnalyticsQueue, JSON.stringify(stored));
}

function scheduleRetry(delay: number): void {
  if (retryTimer !== undefined) return;
  retryTimer = setTimeout(() => {
    retryTimer = undefined;
    void flush("interval");
  }, delay);
}

/**
 * Send what is queued. Never throws, never rejects.
 *
 * A 4xx other than 408/429 drops the batch: the request itself was wrong, so
 * retrying it just poisons the head of the queue forever. That is the same reasoning
 * the query client's retry predicate uses. Everything else backs off.
 */
export async function flush(reason: FlushReason): Promise<void> {
  if (inFlight || pending.length === 0 || meta === null) return;
  if (reason === "navigation" && Date.now() - lastFlushAt < MIN_FLUSH_GAP_MS) {
    return;
  }

  inFlight = true;
  try {
    for (
      let sent = 0;
      sent < MAX_BATCHES_PER_FLUSH && pending.length > 0;
      sent++
    ) {
      const batch = pending.slice(0, FLUSH_BATCH);
      // Persist before the request, not after — see the module docblock.
      await persistNow();
      await sendAnalyticsEvents({
        ...meta,
        sent_at: new Date().toISOString(),
        events: batch,
      });
      pending = pending.slice(batch.length);
      failures = 0;
      lastFlushAt = Date.now();
      await persistNow();
    }
  } catch (error) {
    const permanent =
      error instanceof ApiError &&
      error.status < 500 &&
      error.status !== 408 &&
      error.status !== 429;
    if (permanent) {
      pending = pending.slice(FLUSH_BATCH);
      await persistNow();
    } else {
      failures += 1;
      scheduleRetry(Math.min(1000 * 2 ** failures, MAX_BACKOFF_MS));
    }
  } finally {
    inFlight = false;
  }
}

export function startFlushTimer(): void {
  if (intervalTimer !== undefined) return;
  intervalTimer = setInterval(() => void flush("interval"), FLUSH_INTERVAL_MS);
}

export function stopFlushTimer(): void {
  if (intervalTimer !== undefined) clearInterval(intervalTimer);
  intervalTimer = undefined;
  if (retryTimer !== undefined) clearTimeout(retryTimer);
  retryTimer = undefined;
}

/** Throw the queue away, in memory and on disk. Sign-out and account deletion. */
export async function discardQueue(): Promise<void> {
  stopFlushTimer();
  if (persistTimer !== undefined) {
    clearTimeout(persistTimer);
    persistTimer = undefined;
  }
  pending = [];
  owner = null;
  meta = null;
  failures = 0;
  lastFlushAt = 0;
  inFlight = false;
  await removeItem(StorageKey.AnalyticsQueue);
}

/** Reset module state without touching storage. Tests only. */
export function resetQueueForTests(): void {
  stopFlushTimer();
  if (persistTimer !== undefined) clearTimeout(persistTimer);
  persistTimer = undefined;
  pending = [];
  owner = null;
  meta = null;
  failures = 0;
  lastFlushAt = 0;
  inFlight = false;
}
